import { principles, testimonials } from "@/mocks/about";
import { categories } from "@/mocks/categories";
import { posts } from "@/mocks/posts";
import PageHero from "@/components/feature/PageHero";
import SectionHeading from "@/components/base/SectionHeading";
import ContactForm from "@/pages/about/components/ContactForm";
import JsonLd from "@/components/feature/JsonLd";
import usePageMeta from "@/hooks/usePageMeta";

export default function About() {
  usePageMeta({
    title: "关于我们 - 内容原则与联系方式 | 黑料每日",
    description:
      "了解黑料每日的品牌定位、内容原则与整理流程，并通过联系表单提交线索或纠错反馈，我们会每日整理时段统一查看。",
    canonical: "https://ranqiwangyou.cn/about",
  });

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "黑料每日",
          url: "https://ranqiwangyou.cn/",
          description:
            "每日整理娱乐热点、更新帖与汇总清单，并提供历史归档回溯的内容聚合平台。",
          email: "contact@heiliaodaily.example",
          inLanguage: "zh-CN",
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "关于我们 · 黑料每日",
          inLanguage: "zh-CN",
          url: "https://ranqiwangyou.cn/about",
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://ranqiwangyou.cn/" },
            { "@type": "ListItem", position: 2, name: "关于我们" },
          ],
        }}
      />

      <PageHero
        eyebrow="关于我们"
        title="把每天的热点，整理成看得懂的清单"
        desc="黑料每日是一个以整理为主的内容平台。我们不生产话题，只负责把散落各处的信息按时间与主题归拢好，让你少刷一点、多懂一点。"
        crumbs={[{ label: "关于我们" }]}
      />

      <section className="w-full px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              <i className="ri-quill-pen-line"></i>
              品牌介绍
            </span>
            <h2 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
              一个为「长期蹲守」准备的资讯站点
            </h2>
            <div className="mt-5 space-y-4 text-base leading-loose text-foreground-700">
              <p>
                我们注意到，很多关注娱乐动态的读者并不缺信息，缺的是「看得完整」。一个话题往往今天刷到一半、明天又换了方向，回头想找原委，却不知道从哪翻起。
              </p>
              <p>
                黑料每日想解决的正是这件事。我们把每日新增的内容放进更新帖，按主题整理之后放进汇总清单，再全部归入归档中心。三条路径配合，一个话题从出现到后续变化，都能顺下来。
              </p>
              <p>
                站内内容以整理和陈述为主，尽量不替读者下结论。涉及具体主体时多用泛化表述，未经官方确认的信息会明确标注，不当作既定事实。
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="h-72 w-full overflow-hidden rounded-xl bg-background-200 md:h-96">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20cream%20toned%20editorial%20photo%20of%20a%20tidy%20writing%20desk%20with%20stacked%20newspapers%2C%20a%20notebook%2C%20a%20cup%20of%20tea%20and%20a%20small%20potted%20plant%20in%20soft%20amber%20morning%20light%2C%20minimal%20calm%20mood%2C%20beige%20and%20charcoal%20palette%2C%20no%20people&width=1000&height=1200&seq=about-editorial-desk&orientation=portrait"
                alt="黑料每日内容整理工作台示意"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="w-full px-4 py-10 md:px-6 md:py-12">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            {[
              { num: String(posts.length), label: "已归档条目" },
              { num: String(categories.length), label: "内容方向" },
              { num: "每日", label: "更新频率" },
              { num: "全量", label: "归档范围" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-heading text-3xl font-black text-primary-600 md:text-4xl">
                  {stat.num}
                </p>
                <p className="mt-1.5 text-sm text-foreground-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="内容原则"
          title="我们怎么对待每一条内容"
          desc="这些原则不是口号，而是每一天整理时都要走一遍的流程。"
        />
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {principles.map((item) => (
            <div
              key={item.title}
              className="rounded-lg border border-background-200 bg-background-50 p-6"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                <i className={`${item.icon} text-2xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="w-full px-4 py-12 md:px-6 md:py-16">
          <SectionHeading
            eyebrow="用户口碑"
            title="听听长期读者怎么说"
            desc="以下评价来自站内读者的公开留言，我们只做了必要的整理。"
          />
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {testimonials.map((item) => (
              <figure
                key={item.name}
                className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <i className="ri-double-quotes-l text-2xl text-primary-400"></i>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {item.text}
                </blockquote>
                <figcaption className="mt-4 border-t border-background-200 pt-3.5">
                  <p className="text-sm font-semibold text-foreground-950">{item.name}</p>
                  <p className="text-xs text-foreground-500">{item.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="w-full px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="联系方式"
              title="有线索或建议，随时找我们"
              desc="无论是发现了新的线索，还是发现我们哪里写错了，都欢迎直接说。你的反馈会直接影响我们的整理方向。"
            />
            <ul className="mt-7 space-y-4">
              <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">邮箱联系</p>
                  <p className="mt-0.5 text-sm text-foreground-600">
                    contact@heiliaodaily.example
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">查看时段</p>
                  <p className="mt-0.5 text-sm text-foreground-600">
                    每日整理时段统一查看留言，重要反馈会优先跟进
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                  <i className="ri-shield-check-line text-lg"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">隐私说明</p>
                  <p className="mt-0.5 text-sm text-foreground-600">
                    联系方式仅用于回复你的留言，不会用于其他用途
                  </p>
                </div>
              </li>
            </ul>
          </div>
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
}